import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'

/**
 * Site → store outbound conversion event.
 *
 * `store_outbound_tapped { store, slot }` fires when a visitor clicks any
 * store link (map capsule, header "Get the App" CTA, promo badges, the
 * Android install banner) — direct or through a `/go/site-*` tracking link. It is the site's
 * ONE conversion — the moment a search visitor leaves for the App Store /
 * Google Play — and is registered as a GA4 key event, closing the
 * "search → install" loop that the TASK-644 campaign slots (`ct=`, Play
 * install-referrer) only cover on the store side.
 *
 * Web-only event: the mobile apps have no "leave for the store" surface,
 * so this name is NOT part of the cross-platform contract (unlike the
 * `mapSurfaceEvents` names). `slot` reuses the `StoreLinks.SLOTS` values
 * verbatim so GA segments line up with the store-side campaign reports.
 */
export type StoreKind = 'app_store' | 'google_play'

/**
 * The store a "get the app" CTA names for this visitor: Google Play on
 * Android, the App Store everywhere else (iOS is the portfolio's primary
 * store, and iPadOS Safari reports a macOS user agent anyway). Decided from
 * the user agent, not the href — since TASK-1170 a phone's CTA is a `/go/`
 * tracking link that names no store.
 */
export function storeKindForUserAgent(userAgent: string): StoreKind {
  return /android/i.test(userAgent) ? 'google_play' : 'app_store'
}

/** `store_outbound_tapped { store, slot }` — visitor left for a store. */
export function reportStoreOutboundTapped(
  reporter: MetroAnalyticsReporter,
  params: { store: StoreKind; slot: string },
): void {
  reporter('store_outbound_tapped', { store: params.store, slot: params.slot })
}
